// src/components/DashboardCalendar.jsx
//
// Calendario mensual de entregas para el Dashboard.
// Muestra tickets de departamentos con slaMode=deliveryDate agrupados por día.
// Cada día con entregas muestra dots de color según status. Al hacer click en
// un día se despliega la lista de tickets debajo del calendario.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Skeleton from './Skeleton';

const DAYS   = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const STATUS_COLOR = { open: '#2563eb', in_progress: '#d97706', closed: '#059669' };
const STATUS_LABEL = { open: 'Abierto', in_progress: 'En progreso', closed: 'Resuelto' };
const PRIO_LABEL   = { low: 'Baja', medium: 'Media', high: 'Alta', urgent: 'Urgente' };

function toMonthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function toDayKey(dateStr) {
    // Usar UTC para evitar desfase de zona horaria (la fecha se guarda como midnight UTC)
    const d = new Date(dateStr);
    return d.getUTCDate();
}

/** Fecha de referencia para el calendario: deliveryDate si existe (deptos con
 *  modo deliveryDate), si no dueAt (cualquier otro modo). Esto permite que
 *  TODOS los deptos aparezcan en el calendario, no solo los de modo deliveryDate. */
function ticketDate(t) {
    return t.deliveryDate || t.dueAt;
}

export default function DashboardCalendar() {
    const navigate = useNavigate();
    const [viewDate,   setViewDate]   = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [tickets,    setTickets]    = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [selectedDay, setSelectedDay] = useState(null);

    const year        = viewDate.getFullYear();
    const month       = viewDate.getMonth();
    const firstDow    = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthKey    = toMonthKey(viewDate);

    // Fetch tickets del mes
    useEffect(() => {
        let alive = true;
        setLoading(true);
        setSelectedDay(null);
        api.get('/tickets/calendar', { params: { month: monthKey } })
            .then(r => { if (alive) setTickets(Array.isArray(r.data) ? r.data : []); })
            .catch(() => { if (alive) setTickets([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [monthKey]);

    // Agrupar tickets por día (usando fecha de referencia: deliveryDate || dueAt)
    const byDay = {};
    tickets.forEach(t => {
        const dRef = ticketDate(t);
        if (!dRef) return;
        const day = toDayKey(dRef);
        if (!byDay[day]) byDay[day] = [];
        byDay[day].push(t);
    });

    const today = new Date();
    const isToday = (d) =>
        today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
    const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
    const goToday   = () => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

    const dayTickets = selectedDay ? (byDay[selectedDay] || []) : [];

    return (
        <div style={{
            background: 'var(--c-card)',
            border: '1px solid var(--c-border)',
            borderRadius: 12,
            boxShadow: '0 1px 4px rgba(0,0,0,.06)',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--c-card-header-border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
                <p style={{
                    fontSize: 13, fontWeight: 700, color: 'var(--c-text-2)', margin: 0,
                }}>
                    Calendario de entregas
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                        onClick={goToday}
                        style={{
                            background: 'var(--c-msg-bg)', border: '1px solid var(--c-border)',
                            borderRadius: 6, padding: '2px 8px', fontSize: 11,
                            fontWeight: 600, color: 'var(--c-text-3)', cursor: 'pointer',
                        }}
                    >
                        Hoy
                    </button>
                    <button onClick={prevMonth} style={navBtnStyle} aria-label="Mes anterior">‹</button>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-1)', minWidth: 130, textAlign: 'center' }}>
                        {MONTHS[month]} {year}
                    </span>
                    <button onClick={nextMonth} style={navBtnStyle} aria-label="Mes siguiente">›</button>
                </div>
            </div>

            <div style={{ padding: '16px 20px' }}>
                {loading ? (
                    <Skeleton variant="block" height={240} />
                ) : (
                    <>
                        {/* Encabezado días */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 6 }}>
                            {DAYS.map(d => (
                                <div key={d} style={{
                                    textAlign: 'center', fontSize: 10, fontWeight: 700,
                                    color: 'var(--c-text-4)', padding: '2px 0',
                                    textTransform: 'uppercase', letterSpacing: 0.5,
                                }}>
                                    {d}
                                </div>
                            ))}
                        </div>

                        {/* Grid de días */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                            {cells.map((day, i) => {
                                if (day === null) return <div key={`_${i}`} />;

                                const dayItems  = byDay[day] || [];
                                const hasItems  = dayItems.length > 0;
                                const selected  = selectedDay === day;
                                const hoy       = isToday(day);
                                const hasOverdue = dayItems.some(t =>
                                    t.status !== 'closed' && new Date(ticketDate(t)) < today
                                );

                                return (
                                    <div
                                        key={day}
                                        onClick={() => hasItems && setSelectedDay(selected ? null : day)}
                                        style={{
                                            textAlign: 'center',
                                            padding: '6px 2px 4px',
                                            borderRadius: 8,
                                            cursor: hasItems ? 'pointer' : 'default',
                                            background: selected
                                                ? 'var(--c-status-open-bg, #eff6ff)'
                                                : hoy
                                                    ? 'var(--c-msg-bg)'
                                                    : 'transparent',
                                            border: selected
                                                ? '2px solid #2563eb'
                                                : hoy
                                                    ? '2px solid var(--c-border)'
                                                    : '2px solid transparent',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        <div style={{
                                            fontSize: 12,
                                            fontWeight: hoy ? 700 : hasItems ? 600 : 400,
                                            color: hoy ? '#2563eb'
                                                : hasOverdue ? '#dc2626'
                                                : hasItems ? 'var(--c-text-1)'
                                                : 'var(--c-text-3)',
                                        }}>
                                            {day}
                                        </div>
                                        {/* Dots por cada ticket */}
                                        {hasItems && (
                                            <div style={{
                                                display: 'flex', justifyContent: 'center',
                                                gap: 2, marginTop: 2, flexWrap: 'wrap',
                                            }}>
                                                {dayItems.slice(0, 4).map(t => (
                                                    <span key={t._id} style={{
                                                        width: 6, height: 6, borderRadius: '50%',
                                                        background: t.status !== 'closed' && new Date(ticketDate(t)) < today
                                                            ? '#dc2626'
                                                            : (STATUS_COLOR[t.status] || '#6b7280'),
                                                        display: 'inline-block',
                                                    }} />
                                                ))}
                                                {dayItems.length > 4 && (
                                                    <span style={{ fontSize: 8, color: 'var(--c-text-4)', fontWeight: 700 }}>
                                                        +{dayItems.length - 4}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Leyenda de colores */}
                        <div style={{
                            display: 'flex', gap: 14, marginTop: 12,
                            paddingTop: 10, borderTop: '1px solid var(--c-border)',
                            flexWrap: 'wrap',
                        }}>
                            {[
                                { color: '#2563eb', label: 'Abierto' },
                                { color: '#d97706', label: 'En progreso' },
                                { color: '#059669', label: 'Resuelto' },
                                { color: '#dc2626', label: 'Vencido' },
                            ].map(l => (
                                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: l.color, display: 'inline-block' }} />
                                    <span style={{ fontSize: 10, color: 'var(--c-text-4)', fontWeight: 600 }}>{l.label}</span>
                                </div>
                            ))}
                            <span style={{ fontSize: 10, color: 'var(--c-text-4)', marginLeft: 'auto' }}>
                                {tickets.length} entrega{tickets.length !== 1 ? 's' : ''} en {MONTHS[month].toLowerCase()}
                            </span>
                        </div>
                    </>
                )}
            </div>

            {/* Lista de tickets del día seleccionado */}
            {selectedDay && dayTickets.length > 0 && (
                <div style={{
                    borderTop: '1px solid var(--c-border)',
                    padding: '12px 20px 16px',
                    background: 'var(--c-msg-bg)',
                }}>
                    <p style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.5, color: 'var(--c-text-4)', margin: '0 0 8px',
                    }}>
                        {selectedDay} de {MONTHS[month]} — {dayTickets.length} entrega{dayTickets.length !== 1 ? 's' : ''}
                    </p>
                    {dayTickets.map(t => {
                        const overdue = t.status !== 'closed' && new Date(ticketDate(t)) < today;
                        return (
                            <div
                                key={t._id}
                                onClick={() => navigate(`/tickets/${t._id}`)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 10px',
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    marginBottom: 4,
                                    background: 'var(--c-card)',
                                    border: '1px solid var(--c-border)',
                                    transition: 'box-shadow 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                            >
                                {/* Status dot */}
                                <span style={{
                                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                    background: overdue ? '#dc2626' : (STATUS_COLOR[t.status] || '#6b7280'),
                                }} />

                                {/* Folio */}
                                <span style={{
                                    background: '#4f46e5', color: '#fff',
                                    borderRadius: 5, padding: '1px 6px',
                                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                                }}>
                                    #{t.folio || '—'}
                                </span>

                                {/* Title */}
                                <span style={{
                                    flex: 1, fontSize: 13, fontWeight: 600,
                                    color: 'var(--c-text-1)',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {t.title}
                                </span>

                                {/* Status label */}
                                <span style={{
                                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                                    color: overdue ? '#dc2626' : (STATUS_COLOR[t.status] || '#6b7280'),
                                }}>
                                    {overdue ? 'Vencido' : (STATUS_LABEL[t.status] || t.status)}
                                </span>

                                {/* Assigned */}
                                {t.assignedTo?.name && (
                                    <span style={{
                                        fontSize: 10, color: 'var(--c-text-4)', flexShrink: 0,
                                        maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {t.assignedTo.name}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const navBtnStyle = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--c-text-3)', fontSize: 20, padding: '2px 8px', lineHeight: 1,
};
